import { TokenManager } from '@eci-saas/utils'
import { setupLayouts } from 'virtual:generated-layouts'
import { watch } from 'vue'
// eslint-disable-next-line import/no-duplicates
import { createRouter, createWebHistory } from 'vue-router'
// eslint-disable-next-line import/no-duplicates
import { routes } from 'vue-router/auto-routes'
import { useUserStore } from '../stores/user'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: setupLayouts(routes),
})

function hasAuthToken(): boolean {
  const localTokenManager = new TokenManager(true)
  const sessionTokenManager = new TokenManager(false)
  return !!(localTokenManager.getToken() || sessionTokenManager.getToken())
}

// 任意情况跳转登录前，清空所有本地认证与用户相关缓存，避免旧数据导致死循环
function clearAllAuthAndUserData() {
  TokenManager.clearAllTokens()
  useUserStore().reset()
  localStorage.removeItem('contents_user')
  localStorage.removeItem('database')
  localStorage.removeItem('session_timestamp')
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)
    if (k?.startsWith('session_'))
      keysToRemove.push(k)
  }
  keysToRemove.forEach(k => localStorage.removeItem(k))
}

function redirectToAuth(next: (v?: unknown) => void) {
  clearAllAuthAndUserData()
  window.location.href = import.meta.env.VITE_APP_AUTH_URL_CONTENTS
  next(false)
}

// 监听页面从 bfcache 恢复（浏览器回退时触发）
window.addEventListener('pageshow', (event) => {
  if (event.persisted && !hasAuthToken()) {
    clearAllAuthAndUserData()
    window.location.href = import.meta.env.VITE_APP_AUTH_URL_CONTENTS
  }
})

// 路由守卫
router.beforeEach(async (to, _from, next) => {
  const userStore = useUserStore()

  // 等待认证状态初始化完成，添加超时机制防止死锁
  if (!userStore.initialized || userStore.loading) {
    const authInitPromise = new Promise<void>((resolve) => {
      const unwatch = watch(
        [() => userStore.initialized, () => userStore.loading],
        ([initialized, loading]) => {
          if (initialized && !loading) {
            unwatch()
            resolve()
          }
        },
        { immediate: true },
      )
    })

    const timeoutPromise = new Promise<void>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Authentication initialization timeout after 10 seconds'))
      }, 10000)
    })

    try {
      await Promise.race([authInitPromise, timeoutPromise])
    }
    catch (error) {
      console.error('Router guard timeout:', error)
      // 超时情况下，如果是公开路由就继续，否则重定向到登录页
      const publicRoutes = ['/login', '/signup', '/oauth-callback']
      if (publicRoutes.includes(to.path)) {
        next()
        return
      }
      else {
        next('/login')
        return
      }
    }
  }

  // 公开路由，不需要认证
  const publicRoutes = ['/login', '/signup', '/oauth-callback']
  const isPublicRoute = publicRoutes.includes(to.path)

  if (isPublicRoute) {
    if (userStore.isAuthenticated) {
      next('/')
      return
    }
    next()
    return
  }

  // 仅当「TokenManager 无 token 且 store 未认证」时才跳转登录；有 contents_user 等缓存即视为已登录，避免死循环
  const tokenOrStoreAuth = hasAuthToken() || userStore.isAuthenticated
  if (!tokenOrStoreAuth) {
    redirectToAuth(next)
    return
  }

  next()
})
